import { proNavigator } from "single-spa/react-app/ProNavigator";
import { BasicDurations } from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

//** Constants for duplicated reason strings */
// Deduplicated audience
const REASON_DEDUPLICATED_AUDIENCE_WEB_SOURCE =
    "wa.ao.graph.tabs.tooltip.disable.deduplicated.audience.isNonTotalWebSource";
const REASON_DEDUPLICATED_AUDIENCE_DURATION = "wa.ao.graph.tabs.tooltip.disable.deduplicated.audience.duration";
const REASON_DEDUPLICATED_AUDIENCE_GA_APPROVED_DATA =
    "wa.ao.graph.tabs.tooltip.disable.deduplicated.audience.showGAApprovedData";
const REASON_DEDUPLICATED_AUDIENCE_UNBOUNCED = "wa.ao.graph.tabs.tooltip.disable.deduplicated.audience.unBounced";

// New vs returning
const REASON_NEW_VS_RETURNING_WEB_SOURCE = "wa.ao.graph.tabs.tooltip.disable.new.vs.returning.websource";
const REASON_NEW_VS_RETURNING_DURATION = "wa.ao.graph.tabs.tooltip.disable.new.vs.returning.duration";
const REASON_NEW_VS_RETURNING_GA_APPROVED_DATA = "wa.ao.graph.tabs.tooltip.disable.new.vs.returning.showGAApprovedData";
const REASON_NEW_VS_RETURNING_UNBOUNCED = "wa.ao.graph.tabs.tooltip.disable.new.vs.returning.unBounced";

// Unique users
const REASON_UNIQUE_USERS_IS_GA = "wa.ao.graph.tabs.tooltip.disable.unique.users.isGa";
const REASON_UNIQUE_USERS_UNBOUNCED = "wa.ao.graph.tabs.tooltip.disable.unique.users.unBounced";

// Conversion rates
const REASON_CONVERSION_RATES_WEB_SOURCE = "wa.ao.graph.tabs.tooltip.disable.conversion.rates.isNonTotalWebSource";
export const REASON_CONVERSION_RATES_DURATION = "wa.ao.graph.tabs.tooltip.disable.conversion.rates.duration";

// Bounce rate
const REASON_BOUNCE_RATE_UNBOUNCED = "wa.ao.graph.tabs.tooltip.disable.bounce.rate.unBounced";

export type Availability = {
    isNonTotalWebSource?: boolean;
    duration?: string;
    showGAApprovedData?: boolean;
    unBounced?: string;
    isGa?: boolean;
    showBetaBranchData?: boolean;
};

type AvailabilityInfo = {
    isAvailable: boolean;
    reason?: string; // i18n key
};

/**
 * Helper function to generate availability info based on common conditions.
 */
const getAvailabilityInfo = (
    availability: Availability,
    checks: Array<{ condition: boolean; reason: string }>,
): AvailabilityInfo => {
    for (const check of checks) {
        if (check.condition) {
            return { isAvailable: false, reason: check.reason };
        }
    }
    return { isAvailable: true };
};

export const getAvailabilityInfoForDeduplicatedAudience = (availability: Availability): AvailabilityInfo => {
    return getAvailabilityInfo(availability, [
        {
            condition: availability.isNonTotalWebSource,
            reason: REASON_DEDUPLICATED_AUDIENCE_WEB_SOURCE,
        },
        {
            condition: availability.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            reason: REASON_DEDUPLICATED_AUDIENCE_DURATION,
        },
        {
            condition: availability.showGAApprovedData,
            reason: REASON_DEDUPLICATED_AUDIENCE_GA_APPROVED_DATA,
        },
        {
            condition: availability.unBounced === "true",
            reason: REASON_DEDUPLICATED_AUDIENCE_UNBOUNCED,
        },
    ]);
};

export const getAvailabilityInfoForNewVsReturning = (availability: Availability): AvailabilityInfo => {
    const { webSource } = proNavigator.getParams();
    return getAvailabilityInfo(availability, [
        {
            condition: webSource === devicesTypes.MOBILE || webSource === devicesTypes.DESKTOP,
            reason: REASON_NEW_VS_RETURNING_WEB_SOURCE,
        },
        {
            condition: availability.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            reason: REASON_NEW_VS_RETURNING_DURATION,
        },
        {
            condition: availability.showGAApprovedData,
            reason: REASON_NEW_VS_RETURNING_GA_APPROVED_DATA,
        },
        {
            condition: availability.unBounced === "true",
            reason: REASON_NEW_VS_RETURNING_UNBOUNCED,
        },
    ]);
};

export const getAvailabilityInfoForUniqueUsers = (availability: Availability): AvailabilityInfo => {
    return getAvailabilityInfo(availability, [
        {
            condition: availability.isGa,
            reason: REASON_UNIQUE_USERS_IS_GA,
        },
        {
            condition: availability.unBounced === "true",
            reason: REASON_UNIQUE_USERS_UNBOUNCED,
        },
    ]);
};

export const getAvailabilityInfoForConversionRates = (availability: Availability): AvailabilityInfo => {
    return getAvailabilityInfo(availability, [
        {
            condition: availability.isNonTotalWebSource,
            reason: REASON_CONVERSION_RATES_WEB_SOURCE,
        },
        {
            condition: availability.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            reason: REASON_CONVERSION_RATES_DURATION,
        },
    ]);
};

export const getAvailabilityInfoForBounceRate = (availability: Availability): AvailabilityInfo => {
    return getAvailabilityInfo(availability, [
        {
            condition: availability.unBounced === "true",
            reason: REASON_BOUNCE_RATE_UNBOUNCED,
        },
    ]);
};
